import { withErrorBoundary } from '@s4l/utils/hocs/withErrorBoundary'
import React, { useEffect, useState } from 'react'
import { Button, Typography } from '@s4l/ds'
import { IconPeople } from '@s4l/icons/IconPeople'
import { useS4LContext } from '@s4l/react-context'
import Image from '../Image'
import { User } from '@s4l/stayforlong/auth/User'
import styles from './index.module.css'
import { cn } from '@s4l/utility-css'
import { BreakpointSizes, useBrowser } from '@s4l/utils/hooks/useBrowser'

interface ILoginButtonProps {
  userJSON?: ReturnType<User['toJSON']>
  onClick?: () => void
  variant?: 'text-on-all-devices' | 'standard'
  size?: 'small' | 'medium'
  disabled?: boolean
  customGreetings?: string
}

function LoginButton({
  userJSON,
  onClick,
  variant = 'standard',
  size = 'small',
  customGreetings,
  ...props
}: Readonly<ILoginButtonProps>) {
  const { i18n } = useS4LContext()
  const { activeBreakpoint } = useBrowser()

  const [hasAvatar, setHasAvatar] = useState(false)
  const [imageSrc, setImageSrc] = useState<string | undefined>()

  const user = userJSON ? User.fromJSONOrNull(userJSON) : null

  useEffect(() => {
    const abortController = new AbortController()
    setHasAvatar(false)
    setImageSrc(undefined)
    user?.avatar().then(avatar => {
      if (!abortController.signal.aborted && avatar) {
        setHasAvatar(true)
        setImageSrc(avatar)
      }
    })
    return () => abortController.abort()
  }, [user?.googleAccessToken]) // eslint-disable-line react-hooks/exhaustive-deps

  const typographySizes = {
    small: {
      variant: 'body',
      size: 'm',
    },
    medium: {
      variant: 'title',
      size: 'xs',
    },
  } as const

  const showText = variant === 'text-on-all-devices' || activeBreakpoint >= BreakpointSizes.L

  if (user) {
    return (
      <button
        type="button"
        className={cn(styles.buttonLogged, {
          [styles.buttonLoggedMedium]: size === 'medium',
        })}
        onClick={onClick}
        {...props}
      >
        {hasAvatar && (
          <span className={styles.avatarCircle}>
            <Image width={40} height={40} src={imageSrc ?? ''} alt="avatar" />
          </span>
        )}

        {!hasAvatar && (
          <span
            className={cn(styles.avatarCircle, {
              [styles.initials]: !hasAvatar,
              [styles.icon]: !user.hasName(),
            })}
          >
            {user.hasName() ? (
              <Typography data-testid="initials" as="span" variant="title" size="xs" bold>
                {user.getInitials()}
              </Typography>
            ) : (
              <IconPeople data-testid="icon" />
            )}
          </span>
        )}
        {(user.hasName() || customGreetings) && showText && (
          <Typography
            data-testid="name"
            className={['mr-8', 'align-start']}
            as="span"
            bold
            ellipsis
            {...typographySizes[size]}
          >
            {customGreetings || user.name}
          </Typography>
        )}

        {showText && size === 'medium' && (
          <Typography
            data-testid="name"
            className={['mr-8']}
            as="p"
            variant="caption"
            size="l"
            ellipsis
          >
            {user.email}
          </Typography>
        )}
      </button>
    )
  }

  return (
    <Button
      variant={showText ? 'outlined-rounded' : 'icon-rounded'}
      leadingIcon={<IconPeople />}
      size="small"
      onClick={onClick}
      {...props}
    >
      {i18n.formatMessage({ id: 'global.login' })}
    </Button>
  )
}

LoginButton.displayName = 'LoginButton'
export default withErrorBoundary(LoginButton)
